import { useTranslation } from "react-i18next";
import cx from "classnames";
import { UsageAccountabilityExplorerDto } from "../../../types/analytics/AnalyticsPageDataDto";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import UsageCompletionBar from "./UsageCompletionBar";
import UsageManagerNudgeCell from "./UsageManagerNudgeCell";

const AGING_THRESHOLD_DAYS = 14;

interface UsageExplorerManagerCardListProps {
  rows: UsageAccountabilityExplorerDto["managerRows"];
  completedColor: string;
  onSelectManager: (managerEmployeeId: number) => void;
  filters?: AnalyticsFilterInputs | null;
  canSendNudges?: boolean;
}

function UsageExplorerManagerCardList({
  rows,
  completedColor,
  onSelectManager,
  filters = null,
  canSendNudges = false,
}: UsageExplorerManagerCardListProps) {
  const { t } = useTranslation();

  return (
    <ul className="divide-y divide-gray-200">
      {rows.map((row) => {
        const oldestDays =
          row.oldestOutstandingAgeDays ?? row.oldestWaitingOnManagerAgeDays;
        const ageTone =
          oldestDays !== null && oldestDays >= AGING_THRESHOLD_DAYS
            ? "text-red-600"
            : "text-gray-600";

        return (
          <li key={row.managerEmployeeId} className="px-4 py-4">
            <button
              type="button"
              id={`usage-manager-row-${row.managerEmployeeId}`}
              className="flex w-full flex-col gap-2 text-left hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)]"
              onClick={() => onSelectManager(row.managerEmployeeId)}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold text-gray-800">
                  {row.managerName ||
                    t(
                      "Analytics.Widgets.Usage.Explorer.UnknownManager",
                      "Unknown manager",
                    )}
                </span>
                {oldestDays !== null && (
                  <span className={cx("shrink-0 text-sm font-medium", ageTone)}>
                    {t(
                      "Analytics.Widgets.Usage.Explorer.MobileOldestAge",
                      "{{count}}d oldest",
                      { count: oldestDays },
                    )}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {t(
                  "Analytics.Widgets.Usage.Explorer.MobileManagerSummary",
                  "{{outstanding}} outstanding · team of {{teamSize}}",
                  {
                    outstanding: row.outstandingCount,
                    teamSize: row.teamSize,
                  },
                )}
              </p>
              <UsageCompletionBar
                percent={row.completionPercent}
                fillColor={completedColor}
              />
            </button>
            {canSendNudges && row.outstandingCount > 0 && (
              <div className="mt-2 flex justify-end">
                <UsageManagerNudgeCell row={row} filters={filters} />
              </div>
            )}
          </li>
        );
      })}
      {rows.length === 0 && (
        <li className="px-4 py-10 text-center text-sm text-gray-600">
          {t(
            "Analytics.Widgets.Usage.Explorer.Empty",
            "No rows match this explorer view.",
          )}
        </li>
      )}
    </ul>
  );
}

export default UsageExplorerManagerCardList;
